import type { ReactElement, ReactNode } from "react";
import { Children, cloneElement, useMemo } from "react";

import { cx, styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";

import { Image } from "../image/Image";
import { Slot } from "../utils/Slot";
import { resolveUiColor } from "../utils/colors";
import type { CardProps } from "./Card.types";

type StyleProps = { $borderTop: string; $boxShadow: string };

const StyledCard = styled.div<StyleProps>`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  position: relative;
  background: #ffffff;
  width: 290px;
  border: none;
  border-top: ${({ $borderTop }) => $borderTop};
  box-shadow: ${({ $boxShadow }) => $boxShadow};
  border-radius: 0.28571429rem;
  min-height: 0;
  padding: 0;
  margin: 1em 0;
  transition:
    box-shadow 0.1s ease,
    transform 0.1s ease;
  cursor: default;
  text-decoration: none;
  color: inherit;

  &.is-fluid {
    width: 100%;
  }

  &.is-link {
    cursor: pointer;
  }

  &.is-link:hover {
    box-shadow:
      ${({ $boxShadow }) => $boxShadow},
      0 2px 4px 0 rgba(34, 36, 38, 0.05),
      0 2px 10px 0 rgba(34, 36, 38, 0.05);
    transform: translateY(-2px);
  }

  > img,
  > ${Image} {
    display: block;
    width: 100%;
    height: auto;
    border-radius: 0.28571429rem 0.28571429rem 0 0;
  }
`;

const CardComponent = (props: CardProps) => {
  const {
    asChild,
    fluid,
    raised,
    link,
    href,
    color,
    image,
    children,
    className,
    style,
    onClick,
    ...rest
  } = props;

  const theme = useTheme();
  const slotChild = asChild
    ? (Children.only(children) as ReactElement<{ children?: ReactNode }>)
    : null;
  const isLink = link || !!href || !!onClick;

  const borderTop = useMemo(() => {
    const c = resolveUiColor(theme, color);
    return c ? `2px solid ${c}` : "none";
  }, [theme, color]);

  const boxShadow = raised
    ? "0 2px 4px 0 rgba(34, 36, 38, 0.12), 0 2px 10px 0 rgba(34, 36, 38, 0.15)"
    : "0 1px 3px 0 #d4d4d5, 0 0 0 1px #d4d4d5";

  return (
    <StyledCard
      as={asChild ? Slot : href ? "a" : undefined}
      href={href}
      className={cx(fluid && "is-fluid", isLink && "is-link", className)}
      style={style}
      onClick={onClick}
      $borderTop={borderTop}
      $boxShadow={boxShadow}
      {...(rest as object)}
    >
      {asChild && slotChild
        ? cloneElement(slotChild, undefined, image, slotChild.props.children)
        : [image, children]}
    </StyledCard>
  );
};

export const Card = styled(CardComponent)``;
